import React from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import ButtonLink from "../../components/ButtonLink"
import Translation from "../../components/Translation"
import AccessLock from "../../components/AccessLock"
import { translateMessageId } from "../../utils/translations"
import { Page, Content } from "../../components/SharedStyledComponents"

const Section = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6rem 0;
  gap: 4rem;
  flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};
  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    flex-direction: column;
    margin: 4rem 0;
  }
`

const TextContent = styled.div`
  flex: 1;
  max-width: 540px;
`

const ImageContainer = styled.div`
  flex: 1.2;
  width: 100%;
  max-width: 650px;
  .gatsby-image-wrapper {
    border-radius: 12px;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    border: 1px solid ${(props) => props.theme.colors.lightBorder};
  }
`

const H2 = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 2rem;
`

const Text = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text200};
  margin-bottom: 1.5rem;
`

const StepBadge = styled.div`
  display: inline-block;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 6rem 0;
`

const GencodeAIPage = ({ data }) => {
  const intl = useIntl()

  const heroContent = {
    title: translateMessageId("page-gencode-ai-hero-title", intl),
    header: translateMessageId("page-gencode-ai-hero-header", intl),
    subtitle: translateMessageId("page-gencode-ai-hero-subtitle", intl),
    image: data.hero.childImageSharp.fluid,
    alt: "Gencode AI Developer Agent",
    buttons: [
      {
        content: translateMessageId("page-gencode-ai-btn-portal", intl),
        path: "https://gencode-ai.newinntech.com/login",
      },
    ],
  }

  const badgeText = translateMessageId("page-gencode-ai-badge-step", intl)

  return (
    <AccessLock>
      <Page>
        <PageMetadata
          title="Gencode AI"
          description="Plataforma de generación de código nativo y arquitectura impulsada por IA generativa."
        />
        <PageHero content={heroContent} />

        <Content>
          {/* Step 1 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 1</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-1" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-1" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen1.childImageSharp.fluid}
                alt="Definición de proyecto"
              />
            </ImageContainer>
          </Section>

          {/* Step 2 */}
          <Section reverse>
            <TextContent>
              <StepBadge>{badgeText} 2</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-2" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-2" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen5.childImageSharp.fluid}
                alt="Clasificación de Stack"
              />
            </ImageContainer>
          </Section>

          {/* Step 3 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 3</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-3" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-3" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen6.childImageSharp.fluid}
                alt="Proyección de Costos Cloud"
              />
            </ImageContainer>
          </Section>

          {/* Step 4 */}
          <Section reverse>
            <TextContent>
              <StepBadge>{badgeText} 4</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-4" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-4" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen2.childImageSharp.fluid}
                alt="Validación de Arquitectura"
              />
            </ImageContainer>
          </Section>

          {/* Step 5 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 5</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-5" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-5" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen3.childImageSharp.fluid}
                alt="Esqueleto de Archivos"
              />
            </ImageContainer>
          </Section>

          {/* Step 6 */}
          <Section reverse>
            <TextContent>
              <StepBadge>{badgeText} 6</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-6" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-6" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen4.childImageSharp.fluid}
                alt="Editor de código y chat IA"
              />
            </ImageContainer>
          </Section>

          {/* Step 7 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 7</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-7" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-7" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen7.childImageSharp.fluid}
                alt="Auditoría SecOps"
              />
            </ImageContainer>
          </Section>

          {/* Step 8 */}
          <Section reverse>
            <TextContent>
              <StepBadge>{badgeText} 8</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-8" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-8" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen8.childImageSharp.fluid}
                alt="Pruebas TDD"
              />
            </ImageContainer>
          </Section>

          {/* Step 9 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 9</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-9" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-9" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen9.childImageSharp.fluid}
                alt="Technical README"
              />
            </ImageContainer>
          </Section>

          {/* Step 10 */}
          <Section reverse>
            <TextContent>
              <StepBadge>{badgeText} 10</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-10" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-10" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen11.childImageSharp.fluid}
                alt="Despliegue GitOps"
              />
            </ImageContainer>
          </Section>

          {/* Step 11 */}
          <Section>
            <TextContent>
              <StepBadge>{badgeText} 11</StepBadge>
              <H2>
                <Translation id="page-gencode-ai-step-title-11" />
              </H2>
              <Text>
                <Translation id="page-gencode-ai-step-desc-11" />
              </Text>
            </TextContent>
            <ImageContainer>
              <Img
                fluid={data.screen10.childImageSharp.fluid}
                alt="Resumen Ejecutivo ROI"
              />
            </ImageContainer>
          </Section>

          <CTAContainer>
            <ButtonLink
              to="https://gencode-ai.newinntech.com/login"
              size="large"
            >
              <Translation id="page-gencode-ai-btn-generate" />
            </ButtonLink>
          </CTAContainer>
        </Content>
      </Page>
    </AccessLock>
  )
}

export default GencodeAIPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "gencode_ai_hero.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen1: file(relativePath: { eq: "gencode_screen_1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen2: file(relativePath: { eq: "gencode_screen_2.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen3: file(relativePath: { eq: "gencode_screen_3.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen4: file(relativePath: { eq: "gencode_screen_4.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen5: file(relativePath: { eq: "gencode_screen_5.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen6: file(relativePath: { eq: "gencode_screen_6.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen7: file(relativePath: { eq: "gencode_screen_7.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen8: file(relativePath: { eq: "gencode_screen_8.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen9: file(relativePath: { eq: "gencode_screen_9.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen10: file(relativePath: { eq: "gencode_screen_10.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    screen11: file(relativePath: { eq: "gencode_screen_11.png" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
